import { isEqual } from "lodash";

import { ALL_50_STATES, BLOCKED_FILE_EXTENSIONS } from "@/constants";
import { useDataStore } from "@/stores";

export function parseDocumentsFieldsToProperType(documents) {
  function isValidDate(value) {
    // Check if the value is a string
    let check_1 = typeof value === "string";

    // Check if the value is not purely numeric
    let check_2 = isNaN(Number(value));

    // Validate the format to ensure it looks like a date
    let datePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/; // ISO 8601 pattern
    let check_3 = datePattern.test(value) && !isNaN(Date.parse(value));

    // Check if value is an actual Date object
    let check_4 = value instanceof Date;

    let return_value = (check_1 && check_2 && check_3) || check_4;
    return return_value;
  }

  function parseObject(obj) {
    const parsedObject = {};

    for (const [key, value] of Object.entries(obj)) {
      if (isValidDate(value)) {
        parsedObject[key] = new Date(value).toISOString().slice(0, 10); // Convert to ISO string for date inputs
      } else if (
        value &&
        (key === "ssn" || key === "ein" || key === "account_number" || key === "routing_number")
      ) {
        parsedObject[key] = atob(value); // Decode base64 string
      } else if (typeof value === "object" && value !== null && !Array.isArray(value)) {
        parsedObject[key] = parseObject(value);
      } else if (Array.isArray(value)) {
        parsedObject[key] = value.map((item) => {
          if (isValidDate(item)) {
            return new Date(item).toISOString().slice(0, 10);
          } else if (typeof item === "object" && item !== null) {
            return parseObject(item);
          }
          return item;
        });
      } else {
        if ((key === "state" || key === "ma_state") && value) {
          const state = ALL_50_STATES.find((state) => state.value === value);
          parsedObject[key] = state;
        } else if ((key === "id_info" || key === "suitability_info") && !value) {
          parsedObject[key] = {};
        } else {
          parsedObject[key] = value;
        }
      }
    }

    return parsedObject;
  }

  return documents.map((document) => parseObject(document));
}

export function autoCapitalize(input) {
  return input
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function getObjectDifferences(obj1, obj2) {
  const differences = {};

  function compareObjects(o1, o2, path = "") {
    // Handle null/undefined cases
    if (o1 === o2) return;
    if (o1 == null || o2 == null) {
      differences[path || "root"] = { original: o1, current: o2 };
      return;
    }

    // Get all keys from both objects
    const allKeys = new Set([...Object.keys(o1), ...Object.keys(o2)]);

    allKeys.forEach((key) => {
      const fullPath = path ? `${path}.${key}` : key;
      const val1 = o1[key];
      const val2 = o2[key];

      // Check if key exists in both
      const inO1 = key in o1;
      const inO2 = key in o2;

      if (!inO1 || !inO2) {
        differences[fullPath] = {
          original: inO1 ? val1 : "<<MISSING>>",
          current: inO2 ? val2 : "<<MISSING>>",
        };
        return;
      }

      // Handle different types
      const type1 = typeof val1;
      const type2 = typeof val2;

      if (type1 !== type2) {
        differences[fullPath] = { original: val1, current: val2, note: "type mismatch" };
        return;
      }

      // Handle arrays
      if (Array.isArray(val1) && Array.isArray(val2)) {
        if (!isEqual(val1, val2)) {
          // Changed: only add if NOT equal
          differences[fullPath] = { original: val1, current: val2 };
        }
        return;
      }

      // Handle one being array and other not (edge case)
      if (Array.isArray(val1) || Array.isArray(val2)) {
        differences[fullPath] = { original: val1, current: val2, note: "type mismatch" };
        return;
      }

      // Handle objects (recurse)
      if (type1 === "object" && val1 !== null && val2 !== null) {
        compareObjects(val1, val2, fullPath);
        return;
      }

      // Handle primitives
      if (!Object.is(val1, val2)) {
        differences[fullPath] = { original: val1, current: val2 };
      }
    });
  }

  compareObjects(obj1, obj2);
  return differences;
}

export function extractSigningLinkFromResponse(payload) {
  if (!payload) return null;

  if (typeof payload === "string") {
    return payload;
  }

  if (Array.isArray(payload)) {
    for (const item of payload) {
      const link = extractSigningLinkFromResponse(item);
      if (link) return link;
    }
    return null;
  }

  if (typeof payload !== "object") return null;

  const directLink =
    payload.signing_link ||
    payload.signingLink ||
    payload.embed_src ||
    payload.embed_url ||
    payload.url ||
    payload.link;

  if (typeof directLink === "string") {
    return directLink;
  }

  return (
    extractSigningLinkFromResponse(payload.data) ||
    extractSigningLinkFromResponse(payload.submitter) ||
    extractSigningLinkFromResponse(payload.submitters) ||
    extractSigningLinkFromResponse(payload.signing_links)
  );
}

export function commaSeparateThousands(num) {
  if (!num) return 0;
  // Split the number on the decimal point
  let parts = num.toString().split(".");

  // Add commas to the part before the decimal
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Rejoin the whole number and the decimal part (if it exists)
  return parts.join(".");
}

export function generateRandomId() {
  // Generate a cryptographically secure value
  const array = new Uint8Array(12); //12 gets us 24 length hex like we have with MongoDB, 16 is more secure but adds length to 32 chars
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function commaSeparateThousands_2(num) {
  return num
    .toString()
    .replace(/[^0-9.]/g, "") // Keep only numbers and decimal points
    .replace(/\B(?=(\d{3})+(?!\d))/g, ","); // Add commas for thousands
}

export function convertJsonToCsvFileAndDownload(data, fileName) {
  const convertJsonToCsv = (data) => {
    const array = typeof data != "object" ? JSON.parse(data) : data;

    let maxKeysObject = array.reduce((prev, current) => {
      return Object.keys(current).length > Object.keys(prev).length ? current : prev;
    }, {});

    let headers = Object.keys(maxKeysObject).join(",");
    let str = headers + "\r\n";

    for (let i = 0; i < array.length; i++) {
      let line = "";
      for (let header of Object.keys(maxKeysObject)) {
        if (line !== "") line += ",";
        line += array[i][header] ? array[i][header] : "";
      }
      str += line + "\r\n";
    }

    return str;
  };

  const csvData = convertJsonToCsv(data);
  const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `${fileName}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function formatPhoneNumber(phoneNumber) {
  if (!phoneNumber) return "";

  // Strip all characters from the input except digits
  let cleaned = phoneNumber.replace(/\D/g, "");

  // Limit the number of digits to 10
  if (cleaned.length > 10) {
    cleaned = cleaned.substring(0, 10);
  }

  // Based on the length of the string, add formatting as necessary
  const size = cleaned.length;
  if (size === 0) {
    return cleaned;
  } else if (size < 4) {
    return "(" + cleaned;
  } else if (size < 7) {
    return "(" + cleaned.substring(0, 3) + ") " + cleaned.substring(3, 6);
  } else {
    return (
      "(" +
      cleaned.substring(0, 3) +
      ") " +
      cleaned.substring(3, 6) +
      "-" +
      cleaned.substring(6, 10)
    );
  }
}

export function formatSsn(ssn) {
  if (!ssn) return ssn;

  // Remove all non-numeric characters
  let cleaned = ssn.replace(/\D/g, "");

  // Apply formatting: XXX-XX-XXXX
  if (cleaned.length > 3 && cleaned.length <= 5) {
    cleaned = cleaned.replace(/(\d{3})(\d{1,2})/, "$1-$2");
  } else if (cleaned.length > 5) {
    cleaned = cleaned.replace(/(\d{3})(\d{2})(\d{1,4})/, "$1-$2-$3");
  }

  return cleaned;
}

export function formatEIN(ein) {
  if (!ein) return ein;

  // Remove all non-numeric characters
  let cleaned = ein.replace(/\D/g, "");

  // Apply formatting: XX-XXXXXXX
  if (cleaned.length > 2) {
    cleaned = cleaned.replace(/(\d{2})(\d{1,7})/, "$1-$2");
  }

  return cleaned;
}

export function formatZipCode(zip) {
  if (!zip) return "";

  const cleaned = zip.replace(/\D/g, "");

  if (cleaned.length <= 5) return cleaned;

  return cleaned.substring(0, 5) + "-" + cleaned.substring(5, 9);
}

export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// Investment types that are funded and distributed through a custodian rather than the
// investor's own bank account. Most are IRA variants ("Traditional IRA", "SEP IRA",
// "Beneficiary Roth IRA", ...) but HSAs and Solo 401(k)s get identical handling and don't
// carry "IRA" in their name — a bare type.includes("IRA") silently drops them.
export function isRetirementAccountType(type) {
  if (!type || typeof type !== "string") return false;
  const normalized = type.toLowerCase();
  return normalized.includes("ira") || normalized.includes("hsa") || normalized.includes("401");
}

// function that turns an integer in months into a string in years and months
export function monthsToYears(months) {
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  if (years === 0) {
    return `${remainingMonths} months`;
  } else if (remainingMonths === 0) {
    return `${years} year${years > 1 ? "s" : ""}`;
  } else {
    return `${years} years and ${remainingMonths} months`;
  }
}

export function flattenSchema(schema, config = "default") {
  // Flatten the schema
  const flattened = schema.map((item) => {
    // Destructure the item to separate record_detail_config and the rest of the fields
    const { record_detail_config, ...rest } = item;

    // Extract the required config or default if not provided
    const selectedConfig = record_detail_config[config] || record_detail_config["default"];

    // Merge the selected config fields into the rest of the item fields
    return {
      ...rest,
      ...selectedConfig,
    };
  });
  // Sort the flattened array based on record_detail_group_order
  flattened.sort((a, b) => {
    const orderA = parseInt(a.record_detail_group_order) || 0;
    const orderB = parseInt(b.record_detail_group_order) || 0;
    return orderA - orderB;
  });

  // Return the sorted array
  return flattened;
}

export function validateField(fieldName, value) {
  if (!value) return false;

  const validationRules = {
    phone: (value) => /^\(\d{3}\) \d{3}-\d{4}$/.test(value),
    ssn: (value) => /^\d{3}-\d{2}-\d{4}$/.test(value),
    email: (value) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value),
    ein: (value) => /^\d{2}-\d{7}$/.test(value),
    amount: (value) => /^\d{1,3}(,\d{3})*$/.test(value),
    zip_code: (value) => /^\d{5}(-\d{4})?$/.test(value),
    ma_zip_code: (value) => /^\d{5}(-\d{4})?$/.test(value),
    date_of_birth: (value) => {
      const dob = new Date(value);
      const today = new Date();

      if (isNaN(dob.getTime()) || dob.getFullYear() <= 1900) {
        return false;
      }

      let age = today.getFullYear() - dob.getFullYear();
      const monthDifference = today.getMonth() - dob.getMonth();
      const dayDifference = today.getDate() - dob.getDate();

      if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
        age--;
      }

      return age >= 18;
    },
    id_issued_date: (value) => {
      const date = new Date(value);
      return !isNaN(date.getTime()) && date.getFullYear() > 1900;
    },
    id_expiration_date: (value) => {
      const date = new Date(value);
      return !isNaN(date.getTime()) && date.getFullYear() > 1900;
    },
  };

  const validationRule = validationRules[fieldName];
  if (validationRule) {
    return validationRule(value);
  }

  return true;
}

export function fillMissingMonths(data) {
  const result = [];
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  for (let i = 0; i < data.length - 1; i++) {
    let current = data[i];
    let next = data[i + 1];

    // Add the first object to the result
    if (i === 0) {
      //result.push(current);
    }

    let currentYear = current.year;
    let currentMonth = parseInt(current.month, 10);

    // Generate missing months between current and next
    while (currentYear < next.year || (currentYear === next.year && currentMonth < next.month)) {
      result.push({
        runningTotal: current.runningTotal,
        year: currentYear,
        month: currentMonth < 10 ? "0" + currentMonth : currentMonth.toString(), // Format month with leading zero if necessary
      });

      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }
  }

  // Add the last object to the result
  result.push(data[data.length - 1]);

  // Extend to the current month
  let last = result[result.length - 1];

  let lastYear = last.year;
  let lastMonth = parseInt(last.month, 10);

  while (lastYear < currentYear || (lastYear === currentYear && lastMonth < currentMonth)) {
    lastMonth++;
    if (lastMonth > 12) {
      lastMonth = 1;
      lastYear++;
    }
    result.push({
      runningTotal: last.runningTotal,
      year: lastYear,
      month: lastMonth < 10 ? "0" + lastMonth : lastMonth.toString(), // Format month with leading zero if necessary
    });
  }

  return result;
}

export function isExpirationWithinNext3Months(created_date, institution_name, plaid_account_id) {
  if (!created_date || !institution_name.toLowerCase().includes("pnc") || !plaid_account_id)
    return false;

  const created = new Date(created_date);

  if (created < new Date("2024-10-01")) {
    return false;
  }
  // Add 1 year
  const oneYearAfter = new Date(created);
  oneYearAfter.setFullYear(oneYearAfter.getFullYear() + 1);

  const now = new Date();

  const threeMonthsFromNow = new Date();
  threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);

  return oneYearAfter >= now && oneYearAfter <= threeMonthsFromNow;
}

export function getMonthsUntilExpiration(created_date) {
  if (!created_date) return "0 months";

  const created = new Date(created_date);
  const oneYearAfter = new Date(created);
  oneYearAfter.setFullYear(oneYearAfter.getFullYear() + 1);

  const now = new Date();

  // Calculate months difference
  let yearsDiff = oneYearAfter.getFullYear() - now.getFullYear();
  let monthsDiff = oneYearAfter.getMonth() - now.getMonth();
  let totalMonths = yearsDiff * 12 + monthsDiff;

  // Adjust if the day in 'now' is greater than expiration's day
  if (now.getDate() > oneYearAfter.getDate()) {
    totalMonths -= 1;
  }

  if (totalMonths > 0) {
    return `${totalMonths} month${totalMonths > 1 ? "s" : ""}`;
  }

  // Less than one full month: return days
  const msInDay = 1000 * 60 * 60 * 24;
  const remainingDays = Math.ceil((oneYearAfter - now) / msInDay);

  return `${remainingDays} day${remainingDays > 1 ? "s" : ""}`;
}

export function isValidFile(file) {
  const dataStore = useDataStore();
  if (!file || !file.name) return false;

  const filename = file.name.toLowerCase();

  // Check if filename contains any blocked extension as an actual extension segment
  const hasBlockedExtension = BLOCKED_FILE_EXTENSIONS.some((ext) => {
    const parts = filename.split(".");
    const extWithoutDot = ext.substring(1); // Remove leading dot from ".exe" → "exe"
    return parts.includes(extWithoutDot);
  });

  if (hasBlockedExtension) {
    return false;
  }

  const acceptedFileTypes = Array.isArray(dataStore.accepted_file_types)
    ? dataStore.accepted_file_types
    : [];

  const acceptedFileExtensions = Array.isArray(dataStore.accepted_file_extensions)
    ? dataStore.accepted_file_extensions
    : [];

  // Get the final extension
  const finalMatch = filename.match(/\.(\w+)$/);
  const extension = finalMatch ? `.${finalMatch[1]}` : "";

  const typeValid = acceptedFileTypes.length > 0 ? acceptedFileTypes.includes(file.type) : true;

  const extensionValid =
    !extension || acceptedFileExtensions.length === 0 || acceptedFileExtensions.includes(extension);

  return typeValid && extensionValid;
}

export function formatMonthYear(year, month) {
  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${MONTHS[month - 1]} '${String(year).slice(-2)}`;
}

export function formatCurrency(amount, always_show_cents = false) {}

export function getLastFour(string) {
  if (!string || string.length < 4) return string;
  return string.slice(-4);
}

export function bankIsExpired(created_date, bank, plaid_account_id) {
  //if bank is PNC check if created_date is more than 1 year ago
  if (!bank || !created_date || !plaid_account_id) return false;
  if (bank.toLowerCase().includes("pnc")) {
    const created = new Date(created_date);
    const now = new Date();
    const oneYearAgo = new Date(now.setFullYear(now.getFullYear() - 1));
    return created < oneYearAgo;
  }
  //if bank is not PNC return false
  return false;
}

export function formatID(id) {
  if (!id) return "";
  return id.slice(-4).toUpperCase();
}

export function formatDate(date) {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-US", {
    timeZone: "UTC",
  });
}

// Local-calendar "YYYY-MM-DD". Unlike toISOString().split("T")[0] this never
// shifts the day across a timezone boundary, so a date built for "the 10th"
// still compares equal to a stored distribution_date of "…-10".
export function toLocalISODate(date) {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// Inverse of toLocalISODate: turns a stored date ("2026-08-10", or an ISO
// timestamp) into local midnight. `new Date("2026-08-10")` parses as UTC
// midnight, which reads back as the 9th anywhere west of Greenwich.
export function parseLocalISODate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const [datePart] = String(value).split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  if (!year || !month || !day) return new Date(value);
  return new Date(year, month - 1, day);
}

export function addUTCDays(date, days) {
  const d = new Date(date);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + days));
}

export function formatTerm(term_in_months) {
  if (!term_in_months) return "";
  const years = Math.floor(term_in_months / 12);
  const months = term_in_months % 12;
  let result = "";
  if (years > 0) {
    result += `${years} year${years > 1 ? "s" : ""}`;
  }
  if (months > 0) {
    if (result) result += " and ";
    result += `${months} month${months > 1 ? "s" : ""}`;
  }
  return result;
}

export function getAgeBracket(dateOfBirth) {
  if (!dateOfBirth) return null;
  const birth = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
  ) {
    age--;
  }
  if (age < 30) return "under_30";
  const lower = Math.floor(age / 5) * 5;
  return `${lower}-${lower + 4}`;
}

export {
  serializeAddress,
  parseAddress,
  partitionQuestionnaireGroups,
} from "./suitabilityQuestionnaire";

export {
  SHORT_TERM_COMPOUNDING_DAYS,
  monthlyInterestPayment,
  maturityValue,
  totalInterest,
} from "./interest";
